import { useCollection, useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { getProviderType, type Account } from "@aircap/common/account";
import type { BridgeTransaction } from "@aircap/common/bridge";
import { categories as bridgeCategories, isBridgeCategoryId } from "@aircap/common/bridge";
import { categories, isCategoryId } from "@aircap/common/categories";
import type { GclTransaction } from "@aircap/common/gcl";
import type { PowensTransaction } from "@aircap/common/powens";
import type { SwanTransaction } from "@aircap/common/swan";
import type {
  TrackedCategory,
  TrackedCategoryDirection,
  TrackedCategorySplit,
} from "@aircap/common/trackedCategory";
import { validateTrackedCategorySplits } from "@aircap/common/trackedCategory";
import {
  treezorOperationFullPath,
  type Operation as TreezorOperation,
} from "@aircap/common/treezor";
import { collection, deleteField, doc, updateDoc } from "firebase/firestore";
import { computed, type Ref } from "vue";

export type CategorySelectItem = {
  id: string;
  name: string;
  direction?: TrackedCategoryDirection;
  header?: boolean;
};

export function useTransactionCategories(accountId: Ref<string | undefined>) {
  const { t: $t } = useI18n();

  const accountSource = computed(() =>
    accountId?.value ? doc(collection<Account>(db, "accounts"), accountId.value) : null,
  );
  const { data: account } = useDocument<Account>(accountSource);

  const trackedCategoriesSource = computed(() =>
    accountId?.value
      ? collection<TrackedCategory>(db, `accounts/${accountId.value}/trackedCategories`)
      : null,
  );
  const { data: trackedCategoriesRaw, pending: loadingTrackedCategories } =
    useCollection<TrackedCategory>(trackedCategoriesSource);

  const trackedCategories = computed(
    () => trackedCategoriesRaw.value?.filter((c) => !c.deletedAt) ?? [],
  );

  const trackedCategoryNameById = computed(() => {
    const map = new Map<string, string>();
    for (const c of trackedCategoriesRaw.value ?? []) {
      if (c.deletedAt) map.set(c.id, c.name);
    }
    for (const c of trackedCategories.value) {
      map.set(c.id, c.name);
    }
    return map;
  });

  const hasTrackedCategories = computed(() => trackedCategories.value.length > 0);

  const canSplitCategories = computed(
    () =>
      hasTrackedCategories.value &&
      account.value &&
      getProviderType(account.value.accountType) === "proAccount",
  );

  const categoriesList = computed<CategorySelectItem[]>(() => {
    if (hasTrackedCategories.value) {
      return trackedCategories.value.map((c) => ({
        id: c.id,
        name: c.name,
        direction: c.direction,
      }));
    }

    if (account.value?.accountType === "bridge") return bridgeCategories;
    return categories.map((c) => ({ id: c.id, name: $t(`categories.${c.id}`) }));
  });

  const resolveTrackedCategoryName = (categoryId: string) =>
    trackedCategoryNameById.value.get(categoryId);

  const resolveActiveTrackedCategoryName = (categoryId: string) =>
    trackedCategories.value.find((c) => c.id === categoryId)?.name;

  const directionHeaderLabel = (direction: TrackedCategoryDirection) =>
    direction === "outgoing" ? $t("account.tabs.budget") : $t("account.tabs.commercialisation");

  const getSelectorItems = (amount?: number): CategorySelectItem[] => {
    if (!hasTrackedCategories.value) {
      return categoriesList.value;
    }

    const outgoing = trackedCategories.value.filter((c) => c.direction === "outgoing");
    const incoming = trackedCategories.value.filter((c) => c.direction === "incoming");

    const incomingFirst = (amount ?? 0) > 0;
    const ordered: Array<{
      direction: TrackedCategoryDirection;
      cats: typeof trackedCategories.value;
    }> = incomingFirst
      ? [
          { direction: "incoming", cats: incoming },
          { direction: "outgoing", cats: outgoing },
        ]
      : [
          { direction: "outgoing", cats: outgoing },
          { direction: "incoming", cats: incoming },
        ];

    const showHeaders = outgoing.length > 0 && incoming.length > 0;

    const items: CategorySelectItem[] = [];
    for (const { direction, cats } of ordered) {
      if (cats.length === 0) continue;
      if (showHeaders) {
        items.push({
          id: `__header_${direction}__`,
          name: directionHeaderLabel(direction),
          header: true,
        });
      }
      for (const c of cats) {
        items.push({ id: c.id, name: c.name, direction: c.direction });
      }
    }
    return items;
  };

  const categoryItemProps = (item: CategorySelectItem) => ({
    title: item.name,
    value: item.id,
    disabled: item.header ?? false,
    class: item.header ? "text-xs font-medium uppercase text-medium-emphasis" : undefined,
  });

  const getTransactionDocPath = (transactionId: string) => {
    if (!accountId.value || !account.value) return null;

    const bankAccountId = account.value.bankAccountId;

    switch (account.value.accountType) {
      case "swan":
        return `accounts/${accountId.value}/swanTransactions/${transactionId}`;
      case "treezor":
        return treezorOperationFullPath(accountId.value, transactionId);
      case "bridge":
        return bankAccountId
          ? `accounts/${accountId.value}/bridgeAccounts/${bankAccountId}/transactions/${transactionId}`
          : null;
      case "powens":
        return bankAccountId
          ? `accounts/${accountId.value}/powensAccounts/${bankAccountId}/transactions/${transactionId}`
          : null;
      case "gcl":
        return bankAccountId
          ? `accounts/${accountId.value}/gclAccounts/${bankAccountId}/transactions/${transactionId}`
          : null;
    }
  };

  const getTrackedTransactionRef = (transactionId: string) => {
    const docPath = getTransactionDocPath(transactionId);
    if (!docPath) {
      throw new Error("Could not determine transaction document path");
    }

    return doc<
      SwanTransaction | BridgeTransaction | PowensTransaction | GclTransaction | TreezorOperation
    >(db, docPath);
  };

  const setCategory = async (
    transactionId: string,
    categoryId: string | null,
    transactionAmount: number,
  ) => {
    if (hasTrackedCategories.value) {
      const transactionRef = getTrackedTransactionRef(transactionId);

      await updateDoc(transactionRef, {
        trackedCategorySplits: categoryId
          ? [{ trackedCategoryId: categoryId, amount: Math.abs(transactionAmount) }]
          : deleteField(),
        suggestedTrackedCategoryId: deleteField(),
      });
    } else {
      const docPath = getTransactionDocPath(transactionId);
      if (!docPath) {
        throw new Error("Could not determine transaction document path");
      }

      if (account.value?.accountType === "bridge") {
        const transactionRef = doc<BridgeTransaction>(db, docPath);
        if (categoryId === null) {
          await updateDoc(transactionRef, { userCategoryId: deleteField() });
        } else if (isBridgeCategoryId(categoryId)) {
          await updateDoc(transactionRef, { userCategoryId: categoryId });
        } else {
          throw new Error(`Invalid categoryId: ${categoryId}`);
        }
      } else {
        const transactionRef = doc<
          SwanTransaction | PowensTransaction | GclTransaction | TreezorOperation
        >(db, docPath);
        if (categoryId === null) {
          await updateDoc(transactionRef, { categoryId: deleteField() });
        } else if (isCategoryId(categoryId)) {
          await updateDoc(transactionRef, { categoryId });
        } else {
          throw new Error(`Invalid categoryId: ${categoryId}`);
        }
      }
    }
  };

  const setCategorySplits = async (
    transactionId: string,
    splits: TrackedCategorySplit[],
    transactionAmount: number,
  ) => {
    if (!hasTrackedCategories.value) {
      throw new Error("Tracked categories are required to split a transaction");
    }

    const validation = validateTrackedCategorySplits(splits, {
      transactionAmount,
      activeCategoryIds: trackedCategories.value.map((c) => c.id),
    });
    if (!validation.ok) {
      throw new Error(`Invalid category splits: ${validation.reason}`);
    }

    const transactionRef = getTrackedTransactionRef(transactionId);
    await updateDoc(transactionRef, {
      trackedCategorySplits: splits,
      suggestedTrackedCategoryId: deleteField(),
    });
  };

  const acceptSuggestedTrackedCategory = async (
    transactionId: string,
    suggestedTrackedCategoryId: string,
    transactionAmount: number,
  ) => {
    if (!trackedCategories.value.some((c) => c.id === suggestedTrackedCategoryId)) {
      throw new Error(`Suggested category ${suggestedTrackedCategoryId} is not active`);
    }
    const transactionRef = getTrackedTransactionRef(transactionId);
    await updateDoc(transactionRef, {
      trackedCategorySplits: [
        { trackedCategoryId: suggestedTrackedCategoryId, amount: Math.abs(transactionAmount) },
      ],
      suggestedTrackedCategoryId: deleteField(),
    });
  };

  const rejectSuggestedTrackedCategory = async (
    transactionId: string,
    suggestedTrackedCategoryId: string,
  ) => {
    const transactionRef = getTrackedTransactionRef(transactionId);
    await updateDoc(transactionRef, {
      rejectedSuggestedTrackedCategoryId: suggestedTrackedCategoryId,
      suggestedTrackedCategoryId: deleteField(),
    });
  };

  return {
    categoriesList,
    getSelectorItems,
    categoryItemProps,
    setCategory,
    setCategorySplits,
    acceptSuggestedTrackedCategory,
    rejectSuggestedTrackedCategory,
    resolveTrackedCategoryName,
    resolveActiveTrackedCategoryName,
    hasTrackedCategories,
    canSplitCategories,
    loadingTrackedCategories,
    trackedCategories,
  };
}
